import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';

import useUserContext from '../../hooks/use-user-context';
import { createOptionMap } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { containerStyles } from '../../styles/layout';
import { body, headers, richTextStyles } from '../../styles/type';

import TextRenderer from '../TextRenderer';
import { ReactComponent as Caret } from '../../images/icons/caret-down.svg';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};
  padding: ${rem(32)} 0;

  ${media('>tiny')} {
    padding: ${rem(56)} 0;
  }
`;

const ContentWrapper = styled.div`
  ${({ wide }) => (wide ? containerStyles.regular : containerStyles.small)}
`;

const HeadlineWrapper = styled.div`
  display: flex;
  column-gap: ${rem(20)};
  justify-content: ${({ alignment }) => {
    if (alignment === 'Left') return 'flex-start';
    return 'center';
  }};
`;

const Headline = styled.h2`
  ${headers.h2}
  text-align: ${({ alignment }) => {
    if (alignment === 'Left') return 'left';
    return 'center';
  }};
  color: ${colors.blue['800']};
  margin-bottom: ${rem(40)};
`;

const Button = styled.button`
  display: grid;
  place-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
  flex-shrink: 0;
  background-color: ${colors.blue['500']};
  border-radius: 50%;
  padding: 0;
  border: none;
  margin-left: auto;
  color: ${colors.white};
  transition: background-color 200ms ease;
  cursor: pointer;

  ${media('>small')} {
    width: ${rem(56)};
    height: ${rem(56)};
  }

  &:hover {
    background-color: ${colors.blue['800']};
  }

  &[aria-expanded="true"] {
    transform: rotate(-180deg);
  }
`;

const Collapsible = styled.div`
  ${({ collapsible }) => collapsible && `
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
    transition: grid-template-rows 500ms ease;

    &[aria-hidden="false"] {
      grid-template-rows: 1fr;
    }

    &[aria-hidden="true"] {
      visibility: hidden;
    }
  `}
`;

const Content = styled.div`
  ${body.regular}
  ${richTextStyles}
  min-height: 0px;
  text-align: ${({ alignment }) => {
    if (alignment === 'Center') return 'Center';
    return 'left';
  }};
`;

const Text = ({ useH1,
  headline,
  headlineAlignment,
  text,
  textAlignment,
  wide,
  backgroundColor,
  collapsible }) => {
  const data = useStaticQuery(graphql`
    query Text {
      allContentfulOptionSet(filter: {name: {eq: "text"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Container backgroundColor={backgroundColor}>
      <ContentWrapper wide={wide}>
        {headline && (
          <HeadlineWrapper alignment={headlineAlignment}>
            <Headline as={useH1 ? 'h1' : 'h2'}>
              <TextRenderer text={headline} />
            </Headline>
            {collapsible && (
              <Button
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-label={isOpen
                  ? options['text.aria_label_expanded']
                  : options['text.aria_label_collapsed']}
              >
                <Caret />
              </Button>
            )}
          </HeadlineWrapper>
        )}
        {text && (
          <Collapsible collapsible={collapsible} aria-hidden={!isOpen && collapsible}>
            <Content alignment={textAlignment}>
              <TextRenderer text={text} richText />
            </Content>
          </Collapsible>
        )}
      </ContentWrapper>
    </Container>
  );
};

Text.propTypes = {
  useH1: PropTypes.bool,
  headline: PropTypes.string,
  headlineAlignment: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    references: PropTypes.arrayOf(PropTypes.object)
  }),
  textAlignment: PropTypes.string,
  wide: PropTypes.bool,
  backgroundColor: PropTypes.oneOf(['Blue', 'White']),
  collapsible: PropTypes.bool
};

export const query = graphql`
  fragment TextFragment on ContentfulText {
    id
    __typename
    headline
    headlineAlignment
    text {
      raw
      references {
        ...TextRendererAssetFragment
        ...TextRendererButtonFragment
        ...TextRendererFormFragment
        ...TextRendererTrialFormFragment
        ...TextRendererImageFragment
        ...TextRendererMediaFragment
        ...TextRendererVideoFragment
        ...TextRendererWidgetFragment
      }
    }
    textAlignment
    wide
    collapsible
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default Text;
